/**
 * Copyright 2025-2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { cartConstants } from '@/constants/cart';
import type { ShelfPart } from '@/types/legacy';
import {
  getEligibilityDates,
  isWithinEligibilityPeriod,
} from '@/features/checkout/utils/elegibilityUtil';
import { pageTypes } from '@/constants/page';

const isBestSellerBadgeShown = (
  part: Pick<ShelfPart, 'bestSeller' | 'badges'>,
  acesEnabled: boolean | undefined,
  isShelfPage: boolean,
): boolean => {
  return (
    !!(part.bestSeller || part.badges?.includes('BEST_SELLER')) && !!(acesEnabled || isShelfPage)
  );
};

const isBonusRewardsBadgeShown = (
  bonusRewardEligible: boolean | undefined,
  bonusRewardAmount: number | undefined,
  bonusRewardStartDate: string | undefined,
  bonusRewardEndDate: string | undefined,
  isBonusRewardsBadgeEnabled: boolean,
  isUS: boolean,
): boolean => {
  const eligibilityDates = getEligibilityDates(
    bonusRewardStartDate ?? '',
    bonusRewardEndDate ?? '',
  );

  return (
    isBonusRewardsBadgeEnabled &&
    isUS &&
    isWithinEligibilityPeriod(eligibilityDates) &&
    !!bonusRewardEligible &&
    bonusRewardAmount !== undefined
  );
};

const getFulfillmentOption = (
  shipToHomeAvailable: boolean | undefined,
  storePickupAvailable: boolean | undefined,
  defaultFulfillmentOption: string,
): string => {
  const productNotAvailable = !shipToHomeAvailable && !storePickupAvailable;

  if (productNotAvailable) {
    return '';
  } else if (storePickupAvailable && shipToHomeAvailable) {
    if (defaultFulfillmentOption === cartConstants.BOPUS) {
      return cartConstants.STOREORDER;
    }

    if (defaultFulfillmentOption === cartConstants.STH) {
      return cartConstants.ONLINEORDER;
    }

    return '';
  } else if (storePickupAvailable) {
    return cartConstants.STOREORDER;
  } else {
    return cartConstants.ONLINEORDER;
  }
};

type IsRecommendedBadgeShownOptions = {
  pageType: string | undefined;
  isSponsoredCard: boolean | undefined;
  hasRewardsBadge: boolean;
  hasBestSellerBadge: boolean;
  averageRating: string | undefined;
  index: number;
};

const isRecommendedBadgeShown = ({
  pageType,
  isSponsoredCard,
  hasRewardsBadge,
  hasBestSellerBadge,
  averageRating,
  index,
}: IsRecommendedBadgeShownOptions): boolean => {
  const isFirstProduct = index === 0;
  const rating = parseFloat(averageRating ?? '0');

  return (
    isFirstProduct &&
    pageType !== pageTypes.search &&
    !isSponsoredCard &&
    !hasRewardsBadge &&
    !hasBestSellerBadge &&
    rating >= 4.0
  );
};

export {
  getFulfillmentOption,
  isBestSellerBadgeShown,
  isBonusRewardsBadgeShown,
  isRecommendedBadgeShown,
};
